import type React from 'react';
import type { BookDefaultStylesV1, ContentAlign } from './bookDefaults';
import { isNarrowDeviceViewport, type DeviceViewport } from './devicePreview';
import { resolveFontFamily } from './fontFamily';
import { applyBorderValueToSides, normalizeBorderSides } from './borderSides';

type StyleLike = Record<string, unknown> | undefined;

function resolveCssFontFamily(raw: unknown): string | undefined {
  return typeof raw === 'string' ? resolveFontFamily(raw.trim()) : undefined;
}

function withResolvedFontFamily(css: React.CSSProperties, rawFontFamily?: unknown): React.CSSProperties {
  const resolved = resolveCssFontFamily(rawFontFamily);
  return resolved ? { ...css, fontFamily: resolved } : css;
}

export function resolveTextAlignCss(
  align?: ContentAlign | React.CSSProperties['textAlign'],
): Pick<React.CSSProperties, 'textAlign' | 'textAlignLast'> {
  if (align === 'justify-center') {
    return { textAlign: 'justify', textAlignLast: 'center' };
  }
  if (align === 'left' || align === 'center' || align === 'right' || align === 'justify') {
    return { textAlign: align };
  }
  return {};
}

export function inferStoredTextAlign(css: React.CSSProperties): ContentAlign | undefined {
  const textAlign = css.textAlign as ContentAlign | React.CSSProperties['textAlign'];
  if (textAlign === 'justify-center') return 'justify-center';
  if (textAlign === 'justify' && css.textAlignLast === 'center') return 'justify-center';
  if (
    textAlign === 'left' ||
    textAlign === 'center' ||
    textAlign === 'right' ||
    textAlign === 'justify'
  ) {
    return textAlign;
  }
  return undefined;
}

type SpacingInput = {
  padding?: unknown;
  paddingLeft?: unknown;
  paddingRight?: unknown;
  paddingTop?: unknown;
  paddingBottom?: unknown;
  margin?: unknown;
  marginLeft?: unknown;
  marginRight?: unknown;
  marginTop?: unknown;
  marginBottom?: unknown;
  textIndent?: unknown;
};

function spacingNum(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function hasPerSideSpacing(s: SpacingInput, prefix: 'padding' | 'margin'): boolean {
  return ['Left', 'Right', 'Top', 'Bottom'].some((side) => {
    const value = s[`${prefix}${side}` as keyof SpacingInput];
    return value != null && value !== '';
  });
}

/** Map stored textIndent to padding/margin so the whole block shifts inside its box (no text-indent spill). */
export function resolveBlockIndentSpacing(s: SpacingInput): {
  padding?: React.CSSProperties['padding'];
  margin?: React.CSSProperties['margin'];
  boxSizing: 'border-box';
} {
  const indent = spacingNum(s.textIndent);
  const hasPerSidePad = hasPerSideSpacing(s, 'padding');
  const uniformPad = spacingNum(s.padding);
  const hasPerSideMar = hasPerSideSpacing(s, 'margin');
  const uniformMar = spacingNum(s.margin);

  let pt = hasPerSidePad ? spacingNum(s.paddingTop) : uniformPad;
  let pr = hasPerSidePad ? spacingNum(s.paddingRight) : uniformPad;
  let pb = hasPerSidePad ? spacingNum(s.paddingBottom) : uniformPad;
  let pl = hasPerSidePad ? spacingNum(s.paddingLeft) : uniformPad;

  let mt = hasPerSideMar ? spacingNum(s.marginTop) : uniformMar;
  let mr = hasPerSideMar ? spacingNum(s.marginRight) : uniformMar;
  let mb = hasPerSideMar ? spacingNum(s.marginBottom) : uniformMar;
  let ml = hasPerSideMar ? spacingNum(s.marginLeft) : uniformMar;

  if (indent !== 0) {
    const nextPl = pl + indent;
    if (nextPl >= 0) {
      pl = nextPl;
    } else {
      ml += nextPl;
      pl = 0;
    }
  }

  let padding: React.CSSProperties['padding'];
  if (hasPerSidePad || indent !== 0) {
    if (pt || pr || pb || pl) {
      padding = `${pt}px ${pr}px ${pb}px ${pl}px`;
    }
  } else if (uniformPad > 0) {
    padding = uniformPad;
  }

  let margin: React.CSSProperties['margin'];
  if (hasPerSideMar || indent < 0) {
    if (mt || mr || mb || ml) {
      margin = `${mt}px ${mr}px ${mb}px ${ml}px`;
    }
  } else if (uniformMar > 0) {
    margin = uniformMar;
  }

  return { padding, margin, boxSizing: 'border-box' };
}

export const PREVIEW_TEXT_CONTAINMENT: React.CSSProperties = {
  boxSizing: 'border-box',
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'break-word',
};

export function isFullWidthTextAlign(
  textAlign?: ContentAlign | React.CSSProperties['textAlign'],
): boolean {
  return textAlign === 'justify' || textAlign === 'justify-center';
}

/** Full-width column host; children align via flex cross-axis (not width:100% on bordered boxes). */
export function buildAlignPositionHostStyle(
  textAlign?: ContentAlign | React.CSSProperties['textAlign'],
): React.CSSProperties {
  const base: React.CSSProperties = {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  };
  if (textAlign === 'center') return { ...base, alignItems: 'center' };
  if (textAlign === 'right') return { ...base, alignItems: 'flex-end' };
  if (isFullWidthTextAlign(textAlign)) {
    return { ...base, alignItems: 'stretch', ...linePositionStyle(textAlign) };
  }
  return { ...base, alignItems: 'flex-start' };
}

export function buildAlignChromeWidthStyle(
  textAlign?: ContentAlign | React.CSSProperties['textAlign'],
): React.CSSProperties {
  if (isFullWidthTextAlign(textAlign)) return { width: '100%' };
  return { width: 'fit-content', maxWidth: '100%' };
}

export function linePositionStyle(
  textAlign?: ContentAlign | React.CSSProperties['textAlign'],
): React.CSSProperties {
  if (textAlign === 'center') return { alignSelf: 'center', justifySelf: 'center' };
  if (textAlign === 'right') return { alignSelf: 'flex-end', justifySelf: 'end' };
  if (textAlign === 'left') return { alignSelf: 'flex-start', justifySelf: 'start' };
  if (textAlign === 'justify') return { alignSelf: 'stretch', justifySelf: 'stretch', width: '100%' };
  if (textAlign === 'justify-center') {
    // Full-width block; text justification is handled via textAlign/textAlignLast on content.
    return { alignSelf: 'stretch', justifySelf: 'stretch', width: '100%' };
  }
  return {};
}

/** Flex column host so header alignSelf can override page contentAlign. */
export const HEADER_ALIGNMENT_HOST: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  alignItems: 'stretch',
  minWidth: 0,
};

/** Line / block host in page flow — always full column width; text align applies on inner content only. */
export const LINE_BLOCK_HOST_STYLE: React.CSSProperties = {
  width: '100%',
  minWidth: 0,
  alignSelf: 'stretch',
  maxWidth: '100%',
};

export function splitLinePositionCss(css: React.CSSProperties): {
  position: React.CSSProperties;
  content: React.CSSProperties;
  chromeWidth: React.CSSProperties;
} {
  const rawTextAlign = inferStoredTextAlign(css);
  const textAlignCss = resolveTextAlignCss(rawTextAlign);
  const {
    alignSelf: _alignSelf,
    justifySelf: _justifySelf,
    width: _width,
    maxWidth: _maxWidth,
    textAlign: _textAlign,
    ...content
  } = css;
  const position = buildAlignPositionHostStyle(rawTextAlign);
  const chromeWidth = buildAlignChromeWidthStyle(rawTextAlign);
  const textWidth = isFullWidthTextAlign(rawTextAlign) ? { width: '100%' as const } : {};
  return {
    position,
    chromeWidth,
    content: {
      ...content,
      ...textAlignCss,
      ...textWidth,
      display: (content.display as React.CSSProperties['display']) ?? 'block',
      boxSizing: 'border-box' as const,
    },
  };
}

function withLinePosition(css: React.CSSProperties, rawAlign?: ContentAlign): React.CSSProperties {
  const textAlignCss = resolveTextAlignCss(rawAlign);
  const position = linePositionStyle(rawAlign);
  const { textAlign: _textAlign, alignSelf: _alignSelf, justifySelf: _justifySelf, ...rest } = css;
  return { ...rest, ...textAlignCss, ...position };
}

export function asAdvancedCss(s: StyleLike): React.CSSProperties {
  if (!s || typeof s !== 'object') return {};
  const borderWidth = Number(s.borderWidth ?? 0);
  const borderStyle = typeof s.borderStyle === 'string' ? s.borderStyle : 'none';
  const borderColor =
    typeof s.borderColor === 'string' ? s.borderColor : typeof s.color === 'string' ? s.color : undefined;
  const borderValue =
    borderStyle !== 'none' && borderWidth > 0 && borderColor
      ? `${borderWidth}px ${borderStyle} ${borderColor}`
      : undefined;

  let padding: React.CSSProperties['padding'];
  let margin: React.CSSProperties['margin'];
  const indentSpacing = resolveBlockIndentSpacing(s);
  padding = indentSpacing.padding;
  margin = indentSpacing.margin;

  const out: React.CSSProperties = { boxSizing: indentSpacing.boxSizing };
  const fontSize = s.fontSize != null ? `${s.fontSize}px` : undefined;
  if (fontSize) out.fontSize = fontSize;
  if (typeof s.color === 'string' && s.color.trim()) out.color = s.color;
  if (typeof s.backgroundColor === 'string' && s.backgroundColor.trim()) {
    out.backgroundColor = s.backgroundColor;
  }
  if (typeof s.fontFamily === 'string' && s.fontFamily.trim()) out.fontFamily = s.fontFamily.trim();
  if (s.fontWeight != null) out.fontWeight = s.fontWeight as number;
  if (s.fontStyle) out.fontStyle = s.fontStyle as React.CSSProperties['fontStyle'];
  if (typeof s.textDecoration === 'string') out.textDecoration = s.textDecoration;
  if (s.textAlign) Object.assign(out, resolveTextAlignCss(s.textAlign as ContentAlign));
  if (borderValue) {
    applyBorderValueToSides(out, borderValue, normalizeBorderSides(s as { borderSide?: unknown; borderSides?: unknown }));
  }
  if (s.borderRadius != null) out.borderRadius = s.borderRadius as number;
  if (padding !== undefined) out.padding = padding;
  if (margin !== undefined) out.margin = margin;
  if (typeof s.display === 'string') out.display = s.display as React.CSSProperties['display'];
  if (typeof s.width === 'string' || typeof s.width === 'number') out.width = s.width as React.CSSProperties['width'];
  if (typeof s.whiteSpace === 'string') out.whiteSpace = s.whiteSpace as React.CSSProperties['whiteSpace'];
  const lineHeight = Number(s.lineHeight ?? 0);
  if (lineHeight > 0) out.lineHeight = lineHeight;
  if (typeof s.verticalAlign === 'string') {
    out.verticalAlign = s.verticalAlign as React.CSSProperties['verticalAlign'];
  }
  const letterSpacing = Number(s.letterSpacing ?? 0);
  if (letterSpacing !== 0) out.letterSpacing = letterSpacing;

  return out;
}

export function resolveBookTextLineHeight(bookDefaults?: BookDefaultStylesV1): number {
  const n = Number(bookDefaults?.textLineHeight ?? 1);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export function getNestedLineStyleBase(bookDefaults: BookDefaultStylesV1, level: number) {
  if (level >= 2) {
    return (
      bookDefaults.defaultDeepNestedLineStyle ||
      bookDefaults.defaultNestedLineStyle ||
      bookDefaults.defaultLineStyle ||
      {}
    );
  }
  if (level >= 1) {
    return bookDefaults.defaultNestedLineStyle || bookDefaults.defaultLineStyle || {};
  }
  return bookDefaults.defaultLineStyle || {};
}

export function getSpecialStyleBase(
  bookDefaults: BookDefaultStylesV1 | undefined,
  kind: 'grantha' | 'sankalpaBlock' | 'bhed',
): StyleLike {
  if (!bookDefaults) return {};
  if (kind === 'grantha') return bookDefaults.defaultGranthaReferenceStyle || {};
  if (kind === 'sankalpaBlock') return bookDefaults.defaultSankalpaBlockStyle || {};
  return bookDefaults.defaultBhedStyle || {};
}

function resolveFontSize(
  advanced: React.CSSProperties,
  base: StyleLike,
  fontSize: number,
): string | undefined {
  if (advanced.fontSize) return String(advanced.fontSize);
  if (base && base.fontSize != null) return `${base.fontSize}px`;
  if (fontSize > 0) return `${fontSize}px`;
  return undefined;
}

export function buildLineCss(
  bookDefaults: BookDefaultStylesV1,
  level: number,
  fontSize: number,
  defaults?: { textColor?: string; backgroundColor?: string },
): React.CSSProperties {
  const base = getNestedLineStyleBase(bookDefaults, level);
  const advanced = asAdvancedCss(base);
  const rawTextAlign = base.textAlign as ContentAlign | undefined;
  const resolvedFontSize = resolveFontSize(advanced, base, fontSize);
  return withLinePosition(
    withResolvedFontFamily(
    {
      ...advanced,
      ...(resolvedFontSize ? { fontSize: resolvedFontSize } : {}),
      ...(advanced.color == null && defaults?.textColor ? { color: defaults.textColor } : {}),
      ...(advanced.backgroundColor == null && defaults?.backgroundColor
        ? { backgroundColor: defaults.backgroundColor }
        : {}),
      ...(advanced.whiteSpace == null ? { whiteSpace: 'pre-wrap' as const } : {}),
      ...(advanced.display == null ? { display: 'block' as const } : {}),
      ...(advanced.lineHeight == null ? { lineHeight: resolveBookTextLineHeight(bookDefaults) } : {}),
    },
    base.fontFamily,
  ),
    rawTextAlign,
  );
}

export function buildSpecialCss(
  bookDefaults: BookDefaultStylesV1 | undefined,
  kind: 'grantha' | 'sankalpaBlock' | 'bhed',
  fontSize: number,
  defaults?: { textColor?: string; backgroundColor?: string },
): React.CSSProperties {
  const base = getSpecialStyleBase(bookDefaults, kind);
  const advanced = asAdvancedCss(base);
  const rawTextAlign = base?.textAlign as ContentAlign | undefined;
  const resolvedFontSize = resolveFontSize(advanced, base, fontSize);
  return withLinePosition(
    withResolvedFontFamily(
    {
      ...advanced,
      ...(resolvedFontSize ? { fontSize: resolvedFontSize } : {}),
      ...(advanced.color == null && defaults?.textColor ? { color: defaults.textColor } : {}),
      ...(advanced.backgroundColor == null && defaults?.backgroundColor
        ? { backgroundColor: defaults.backgroundColor }
        : {}),
      ...(advanced.whiteSpace == null ? { whiteSpace: 'pre-wrap' as const } : {}),
      ...(advanced.display == null ? { display: 'block' as const } : {}),
      ...(advanced.lineHeight == null
        ? { lineHeight: resolveBookTextLineHeight(bookDefaults) }
        : {}),
    },
    base?.fontFamily,
  ),
    rawTextAlign,
  );
}

export function buildSpecialCssWithOverride(
  bookDefaults: BookDefaultStylesV1 | undefined,
  kind: 'grantha' | 'sankalpaBlock' | 'bhed',
  fontSize: number,
  override: LineOverrideLike | undefined,
  defaults?: { textColor?: string; backgroundColor?: string },
  resolveFontFamily?: (ff?: string) => string | undefined,
  opts?: { editOverlay?: boolean },
): React.CSSProperties {
  const baseCss = buildSpecialCss(bookDefaults, kind, fontSize, defaults);

  if (!override) {
    return opts?.editOverlay ? { ...baseCss, position: 'relative' } : baseCss;
  }

  const base = getSpecialStyleBase(bookDefaults, kind);
  const merged = {
    ...base,
    fontSize: override.fontSize,
    color: override.color,
    backgroundColor: override.backgroundColor,
    fontFamily: override.fontFamily,
    fontWeight: override.fontWeight,
    fontStyle: override.fontStyle,
    textDecoration: override.textDecoration,
    textAlign: override.textAlign,
    borderWidth: override.borderWidth,
    borderStyle: override.borderStyle,
    borderColor: override.borderColor,
    borderRadius: override.borderRadius,
    padding: override.padding,
    paddingLeft: override.paddingLeft,
    paddingRight: override.paddingRight,
    paddingTop: override.paddingTop,
    paddingBottom: override.paddingBottom,
    margin: override.margin,
    marginLeft: override.marginLeft,
    marginRight: override.marginRight,
    marginTop: override.marginTop,
    marginBottom: override.marginBottom,
    lineHeight: override.lineHeight,
    textIndent: override.textIndent,
    width: override.width,
    display: override.display,
    whiteSpace: override.whiteSpace,
    verticalAlign: override.verticalAlign,
    letterSpacing: override.letterSpacing,
  };

  const advanced = asAdvancedCss(merged);
  const resolveFn = resolveFontFamily ?? resolveCssFontFamily;
  const resolvedFont = resolveFn(
    override.fontFamily ?? (typeof merged.fontFamily === 'string' ? merged.fontFamily : undefined),
  );
  const rawTextAlign = (override.textAlign ?? baseCss.textAlign) as ContentAlign | undefined;

  return withLinePosition(
    {
    ...baseCss,
    ...advanced,
    fontSize: advanced.fontSize ?? baseCss.fontSize,
    color: resolveOverrideCssString(override, 'color', advanced, baseCss),
    backgroundColor: resolveOverrideCssString(override, 'backgroundColor', advanced, baseCss),
    fontFamily: resolvedFont ?? advanced.fontFamily ?? baseCss.fontFamily,
    fontWeight: advanced.fontWeight ?? baseCss.fontWeight,
    fontStyle: advanced.fontStyle ?? baseCss.fontStyle,
    textDecoration: advanced.textDecoration ?? baseCss.textDecoration,
    lineHeight: advanced.lineHeight ?? baseCss.lineHeight,
    textIndent: advanced.textIndent ?? baseCss.textIndent,
    width: advanced.width ?? baseCss.width,
    display: advanced.display ?? baseCss.display,
    whiteSpace: advanced.whiteSpace ?? baseCss.whiteSpace,
    verticalAlign: advanced.verticalAlign ?? baseCss.verticalAlign,
    letterSpacing: advanced.letterSpacing ?? baseCss.letterSpacing,
    ...(opts?.editOverlay ? { position: 'relative' as const } : {}),
  },
    rawTextAlign,
  );
}

export function buildLinkCss(
  bookDefaults: BookDefaultStylesV1 | undefined,
  fontSize: number,
  defaultLinkColor?: string,
): React.CSSProperties {
  const base = bookDefaults?.defaultLinkStyle || {};
  const advanced = asAdvancedCss(base);
  const resolvedFontSize = resolveFontSize(advanced, base, fontSize);
  return withResolvedFontFamily(
    {
      ...advanced,
      ...(resolvedFontSize ? { fontSize: resolvedFontSize } : {}),
      ...(advanced.color == null && defaultLinkColor ? { color: defaultLinkColor } : {}),
      height: 'auto',
    },
    base.fontFamily,
  );
}

export type LineOverrideLike = {
  content?: string;
  fontSize?: number;
  color?: string;
  backgroundColor?: string;
  fontFamily?: string;
  fontWeight?: number;
  fontStyle?: 'normal' | 'italic';
  textDecoration?: string;
  textAlign?: ContentAlign;
  borderWidth?: number;
  borderStyle?: 'none' | 'solid' | 'dashed' | 'dotted' | 'double';
  borderColor?: string;
  borderSide?: 'all' | 'top' | 'right' | 'bottom' | 'left';
  borderSides?: Array<'top' | 'right' | 'bottom' | 'left'>;
  borderRadius?: number;
  padding?: number;
  paddingLeft?: number;
  paddingRight?: number;
  paddingTop?: number;
  paddingBottom?: number;
  margin?: number;
  marginLeft?: number;
  marginRight?: number;
  marginTop?: number;
  marginBottom?: number;
  lineHeight?: number;
  textIndent?: number;
  width?: string | number;
  display?: 'block' | 'inline' | 'inline-block';
  whiteSpace?: 'pre-wrap' | 'pre' | 'normal';
  verticalAlign?: 'top' | 'middle' | 'bottom' | 'baseline';
  letterSpacing?: number;
};

function resolveOverrideCssString(
  override: LineOverrideLike,
  key: 'color' | 'backgroundColor' | 'borderColor' | 'fontFamily',
  advanced: React.CSSProperties,
  baseCss: React.CSSProperties,
): string | undefined {
  if (Object.prototype.hasOwnProperty.call(override, key)) {
    const value = override[key];
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }
  const fromAdvanced = advanced[key as keyof React.CSSProperties];
  if (typeof fromAdvanced === 'string' && fromAdvanced.trim()) return fromAdvanced;
  const fromBase = baseCss[key as keyof React.CSSProperties];
  return typeof fromBase === 'string' && fromBase.trim() ? fromBase : undefined;
}

export function buildLineCssWithOverride(
  bookDefaults: BookDefaultStylesV1 | undefined,
  level: number,
  fontSize: number,
  override: LineOverrideLike | undefined,
  defaults?: { textColor?: string; backgroundColor?: string },
  resolveFontFamily?: (ff?: string) => string | undefined,
  opts?: { editOverlay?: boolean },
): React.CSSProperties {
  const baseCss = bookDefaults
    ? buildLineCss(bookDefaults, level, fontSize, defaults)
    : fontSize > 0
      ? { fontSize: `${fontSize}px`, whiteSpace: 'pre-wrap' as const }
      : { whiteSpace: 'pre-wrap' as const };

  if (!override) {
    return opts?.editOverlay ? { ...baseCss, position: 'relative' } : baseCss;
  }

  const merged = {
    ...getNestedLineStyleBase(bookDefaults || ({} as BookDefaultStylesV1), level),
    fontSize: override.fontSize,
    color: override.color,
    backgroundColor: override.backgroundColor,
    fontFamily: override.fontFamily,
    fontWeight: override.fontWeight,
    fontStyle: override.fontStyle,
    textDecoration: override.textDecoration,
    textAlign: override.textAlign,
    borderWidth: override.borderWidth,
    borderStyle: override.borderStyle,
    borderColor: override.borderColor,
    borderRadius: override.borderRadius,
    padding: override.padding,
    paddingLeft: override.paddingLeft,
    paddingRight: override.paddingRight,
    paddingTop: override.paddingTop,
    paddingBottom: override.paddingBottom,
    margin: override.margin,
    marginLeft: override.marginLeft,
    marginRight: override.marginRight,
    marginTop: override.marginTop,
    marginBottom: override.marginBottom,
    lineHeight: override.lineHeight,
    textIndent: override.textIndent,
    width: override.width,
    display: override.display,
    whiteSpace: override.whiteSpace,
    verticalAlign: override.verticalAlign,
    letterSpacing: override.letterSpacing,
  };

  const advanced = asAdvancedCss(merged);
  const resolveFn = resolveFontFamily ?? resolveCssFontFamily;
  const resolvedFont = resolveFn(
    override.fontFamily ?? (typeof merged.fontFamily === 'string' ? merged.fontFamily : undefined),
  );
  const rawTextAlign = (override.textAlign ?? baseCss.textAlign) as ContentAlign | undefined;

  return withLinePosition(
    {
    ...baseCss,
    ...advanced,
    fontSize: advanced.fontSize ?? baseCss.fontSize,
    color: resolveOverrideCssString(override, 'color', advanced, baseCss),
    backgroundColor: resolveOverrideCssString(override, 'backgroundColor', advanced, baseCss),
    fontFamily: resolvedFont ?? advanced.fontFamily ?? baseCss.fontFamily,
    fontWeight: advanced.fontWeight ?? baseCss.fontWeight,
    fontStyle: advanced.fontStyle ?? baseCss.fontStyle,
    textDecoration: advanced.textDecoration ?? baseCss.textDecoration,
    lineHeight: advanced.lineHeight ?? baseCss.lineHeight,
    textIndent: advanced.textIndent ?? baseCss.textIndent,
    width: advanced.width ?? baseCss.width,
    display: advanced.display ?? baseCss.display,
    whiteSpace: advanced.whiteSpace ?? baseCss.whiteSpace,
    verticalAlign: advanced.verticalAlign ?? baseCss.verticalAlign,
    letterSpacing: advanced.letterSpacing ?? baseCss.letterSpacing,
    ...(opts?.editOverlay ? { position: 'relative' as const } : {}),
  },
    rawTextAlign,
  );
}

export function buildNestedLineCssWithOverride(
  bookDefaults: BookDefaultStylesV1 | undefined,
  level: number,
  fontSize: number,
  override: LineOverrideLike | undefined,
  defaults?: { textColor?: string; backgroundColor?: string },
  resolveFontFamily?: (ff?: string) => string | undefined,
  opts?: { editOverlay?: boolean },
): React.CSSProperties {
  const style = buildLineCssWithOverride(
    bookDefaults,
    level,
    fontSize,
    override,
    defaults,
    resolveFontFamily,
    opts,
  );
  return {
    ...style,
    display: style.display ?? 'inline-block',
  };
}

/** Reset inherited header color/font so body lines keep their own styles (color inherits; padding does not). */
export const CONTENT_COLOR_RESET: React.CSSProperties = {
  color: 'initial',
  backgroundColor: 'initial',
  fontFamily: 'initial',
  fontWeight: 'initial',
  fontStyle: 'initial',
  textDecoration: 'initial',
  textAlign: 'initial',
};

export type HeaderStyleOverrideLike = LineOverrideLike;

function pickBoxChrome(css: React.CSSProperties): BoxChromeStyle {
  const { box } = splitBoxChromeFromCss(css);
  return box;
}

export function wrapHeaderStyleLayers(style: React.CSSProperties): {
  position: React.CSSProperties;
  chrome: React.CSSProperties;
} {
  const { box, text } = splitBoxChromeFromCss(style);
  const { position, content, chromeWidth } = splitLinePositionCss(text);
  return {
    position,
    chrome: {
      ...box,
      ...content,
      ...chromeWidth,
      boxSizing: box.boxSizing ?? 'border-box',
      minWidth: 0,
    },
  };
}
function pickBorderChrome(css: React.CSSProperties): Pick<
  React.CSSProperties,
  'border' | 'borderTop' | 'borderRight' | 'borderBottom' | 'borderLeft' | 'borderRadius'
> {
  const out: Pick<
    React.CSSProperties,
    'border' | 'borderTop' | 'borderRight' | 'borderBottom' | 'borderLeft' | 'borderRadius'
  > = {};
  for (const key of ['border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft', 'borderRadius'] as const) {
    const value = css[key];
    if (value !== undefined) out[key] = value;
  }
  return out;
}

export function buildHeaderDisplayStyle(opts: {
  bookDefaults?: BookDefaultStylesV1;
  overrides?: HeaderStyleOverrideLike;
  fontSize: number;
  headerTextColor?: string;
  defaultMarginBottom?: number;
  resolveFontFamily?: (ff?: string) => string | undefined;
}): React.CSSProperties {
  const { bookDefaults, overrides, fontSize, headerTextColor, defaultMarginBottom, resolveFontFamily } = opts;
  const baseRaw = bookDefaults?.defaultHeaderStyle || {};
  const mergedRaw = { ...baseRaw, ...(overrides || {}) };
  const base = asAdvancedCss(baseRaw);
  const advanced = asAdvancedCss(mergedRaw);
  const resolvedFont =
    resolveFontFamily?.(
      typeof mergedRaw.fontFamily === 'string' ? mergedRaw.fontFamily : undefined,
    ) ?? advanced.fontFamily ?? base.fontFamily;
  const rawTextAlign = (mergedRaw.textAlign ?? baseRaw.textAlign) as ContentAlign | undefined;
  const boxChrome = pickBoxChrome(advanced);

  const style: React.CSSProperties = {
    display: (advanced.display as React.CSSProperties['display']) ?? 'block',
    whiteSpace: (advanced.whiteSpace as React.CSSProperties['whiteSpace']) ?? 'pre-wrap',
    fontSize: advanced.fontSize ?? `${fontSize}px`,
    fontFamily: resolvedFont,
    fontWeight: advanced.fontWeight ?? base.fontWeight ?? 600,
    fontStyle: advanced.fontStyle ?? base.fontStyle,
    textDecoration: advanced.textDecoration ?? base.textDecoration,
    color: (advanced.color ?? headerTextColor) || undefined,
    lineHeight: advanced.lineHeight ?? base.lineHeight ?? 1,
    ...boxChrome,
  };

  if (style.margin == null && style.marginBottom == null && defaultMarginBottom != null) {
    style.marginBottom = defaultMarginBottom;
  }

  return withLinePosition(style, rawTextAlign);
}

export function buildHeaderCss(
  bookDefaults: BookDefaultStylesV1,
  fontSize: number,
  headerTextColor?: string,
): React.CSSProperties {
  const headerStyle = bookDefaults.defaultHeaderStyle;
  const base = asAdvancedCss(headerStyle);
  const resolvedFontSize = resolveFontSize(base, headerStyle, fontSize);
  return withResolvedFontFamily(
    {
      ...buildHeaderDisplayStyle({ bookDefaults, fontSize, headerTextColor }),
      ...(resolvedFontSize ? { fontSize: resolvedFontSize } : {}),
    },
    headerStyle?.fontFamily,
  );
}

export const TIGHT_CONTENT_FLOW_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  lineHeight: 1,
  width: '100%',
  minWidth: 0,
};

export function buildContentFlowStyle(bookDefaults?: BookDefaultStylesV1): React.CSSProperties {
  const gap = Math.max(0, bookDefaults?.contentLineGapPx ?? 0);
  const align = bookDefaults?.contentAlign ?? 'left';
  const style: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap,
    lineHeight: resolveBookTextLineHeight(bookDefaults),
  };
  if (align === 'center') {
    style.textAlign = 'center';
    style.alignItems = 'center';
  } else if (align === 'right') {
    style.textAlign = 'right';
    style.alignItems = 'flex-end';
  } else if (align === 'justify') {
    style.textAlign = 'justify';
    style.alignItems = 'stretch';
  } else if (align === 'justify-center') {
    style.textAlign = 'justify';
    style.textAlignLast = 'center';
    style.alignItems = 'stretch';
  } else {
    style.textAlign = 'left';
    style.alignItems = 'stretch';
  }
  return style;
}

export function buildGridContainerStyle(
  bookDefaults: BookDefaultStylesV1 | undefined,
  viewport: DeviceViewport = 'desktop',
): React.CSSProperties | undefined {
  if (isNarrowDeviceViewport(viewport)) return undefined;
  const columns = bookDefaults?.gridColumns ?? 1;
  if (columns !== 2) return undefined;

  const gap = bookDefaults?.gridGapPx;
  const style: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridAutoFlow: 'row dense',
    alignItems: 'start',
  };
  if (gap != null && gap > 0) {
    style.gap = gap;
  }
  return style;
}

export function buildGridItemStyle(
  bookDefaults: BookDefaultStylesV1 | undefined,
  level: number,
  viewport: DeviceViewport = 'desktop',
  spanOverride?: 12 | 24,
): React.CSSProperties {
  if (level > 0 || !bookDefaults || isNarrowDeviceViewport(viewport)) {
    return { minWidth: 0 };
  }
  const span = spanOverride ?? bookDefaults.defaultLineStyle?.span ?? 24;
  return { gridColumn: span === 12 ? 'span 1' : 'span 2', minWidth: 0 };
}

const BOX_CHROME_KEYS = [
  'padding',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'backgroundColor',
  'border',
  'borderTop',
  'borderRight',
  'borderBottom',
  'borderLeft',
  'borderRadius',
  'boxSizing',
] as const satisfies readonly (keyof React.CSSProperties)[];

export type BoxChromeStyle = Pick<React.CSSProperties, (typeof BOX_CHROME_KEYS)[number]>;

export function splitBoxChromeFromCss(css: React.CSSProperties): {
  box: BoxChromeStyle;
  text: React.CSSProperties;
} {
  const box: BoxChromeStyle = {};
  const text: React.CSSProperties = { ...css };
  for (const key of BOX_CHROME_KEYS) {
    const value = css[key];
    if (value !== undefined) {
      (box as Record<string, unknown>)[key] = value;
      delete (text as Record<string, unknown>)[key];
    }
  }
  return { box, text };
}

export function wrapLineBoxStyleLayers(css: React.CSSProperties): {
  host: React.CSSProperties;
  box: React.CSSProperties;
  text: React.CSSProperties;
} {
  const { box, text } = splitBoxChromeFromCss(css);
  const { position, content, chromeWidth } = splitLinePositionCss(text);
  return {
    host: position,
    box: { ...box, ...chromeWidth, boxSizing: 'border-box', minWidth: 0 },
    text: content,
  };
}

export function hasVisibleBorderChrome(box: BoxChromeStyle): boolean {
  const keys = ['border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft'] as const;
  return keys.some((key) => {
    const value = box[key];
    return typeof value === 'string' && value.trim().length > 0;
  });
}

export function applyBoxBorderSelection(
  box: BoxChromeStyle,
  selectionBorder?: string,
): BoxChromeStyle {
  if (!selectionBorder || hasVisibleBorderChrome(box)) return box;
  return { ...box, border: selectionBorder };
}
